import { jwtDecode } from "jwt-decode";
import { defineStore } from "pinia";

import { getAuthClient } from "@/modules/authorisation/authClient";
import { FeatureFlags, Permissions, Roles } from "@/modules/authorisation/enums";

import { Permission, Role, Token } from "./types";

interface DeveloperOverrides {
  permissions: Record<string, boolean>;
  roles: Record<string, boolean>;
  featureFlags: Record<string, boolean>;
}

export const useAuthorisationStore = defineStore("authorisation", {
  persist: true,
  state: (): {
    user?: {
      email?: string;
      name?: string;
      permissions?: Permission[];
      roles?: Role[];
      featureFlags?: { [key: string]: boolean };
    };
    userPreferences?: Record<string, any>;
    redirectToAfterLoginUrl?: string;
    developerOverrides?: DeveloperOverrides;
    tokenExpiresAt?: number;
  } => ({
    user: undefined,
    userPreferences: { isDarkMode: null },
    redirectToAfterLoginUrl: undefined,
    developerOverrides: undefined,
    tokenExpiresAt: undefined,
  }),
  actions: {
    setUserPreference(key: string, value: any) {
      if (!this.userPreferences) {
        this.userPreferences = {};
      }
      this.userPreferences[key] = value;
    },
    async syncClaims() {
      const authClient = await getAuthClient();
      const token = await authClient.getAccessToken();
      if (!token) {
        return;
      }
      await this.onTokenUpdate(token);
    },
    async onTokenUpdate(accessToken: string) {
      const payload = jwtDecode<Token>(accessToken);
      const expiresAt = payload.exp ? payload.exp * 1000 : undefined;

      if (expiresAt && expiresAt === this.tokenExpiresAt && this.user) {
        return;
      }

      const authClient = await getAuthClient();
      const kindeUser = await authClient.getUserProfile();

      this.user = {
        email: kindeUser?.email || undefined,
        name: kindeUser?.given_name || undefined,
        permissions: payload.permissions,
        roles: payload.roles,
        featureFlags: Object.entries(payload?.feature_flags || {})
          .map(([key, value]) => ({ [key]: value.v }))
          .reduce(
            (acc, cur) => ({
              ...acc,
              ...cur,
            }),
            {},
          ),
      };

      this.tokenExpiresAt = expiresAt;
    },
    setDeveloperOverride(
      type: "permission" | "role" | "featureFlag",
      key: Permissions | Roles | FeatureFlags | string,
      enabled: boolean,
    ) {
      if (!this.developerOverrides) {
        this.developerOverrides = {
          permissions: {},
          roles: {},
          featureFlags: {},
        };
      }

      if (type === "permission") {
        const hasPermission = this.user?.permissions?.includes(key);
        if (enabled === hasPermission) {
          delete this.developerOverrides.permissions[key];
        } else {
          this.developerOverrides.permissions[key] = enabled;
        }
      } else if (type === "role") {
        const hasRole = this.user?.roles?.some((r) => r.key === key);
        if (enabled === hasRole) {
          delete this.developerOverrides.roles[key];
        } else {
          this.developerOverrides.roles[key] = enabled;
        }
      } else if (type === "featureFlag") {
        const hasFeature = this.user?.featureFlags?.[key] ?? false;
        if (enabled === hasFeature) {
          delete this.developerOverrides.featureFlags[key];
        } else {
          this.developerOverrides.featureFlags[key] = enabled;
        }
      }
    },
    clearDeveloperOverrides() {
      this.developerOverrides = undefined;
    },
  },
  getters: {
    hasFeature(state) {
      return (flag: FeatureFlags | `${FeatureFlags}`) => {
        if (state.developerOverrides?.featureFlags && flag in state.developerOverrides.featureFlags) {
          return state.developerOverrides.featureFlags[flag];
        }
        return state.user?.featureFlags?.[flag] ?? false;
      };
    },
    hasRole(state) {
      return (role: Roles | `${Roles}`) => {
        if (state.developerOverrides?.roles && role in state.developerOverrides.roles) {
          return state.developerOverrides.roles[role];
        }
        return state.user?.roles?.some(({ key }) => key === role) ?? false;
      };
    },
    can(state) {
      return (permission: Permissions | `${Permissions}`) => {
        if (state.developerOverrides?.permissions && permission in state.developerOverrides.permissions) {
          return state.developerOverrides.permissions[permission];
        }
        return state.user?.permissions?.some((checkPermission) => checkPermission === permission) ?? false;
      };
    },
    getRedirectToAfterLoginUrl(state) {
      if (
        state.redirectToAfterLoginUrl &&
        !["/auth/login", "/404", "/403"].some((path) => state.redirectToAfterLoginUrl!.includes(path))
      ) {
        return state.redirectToAfterLoginUrl;
      }
    },
    isDeveloperModeActive(state) {
      return Boolean(state.developerOverrides);
    },
    getTokenExpiresAt(state) {
      return state.tokenExpiresAt;
    },
  },
});
